import { HttpClient, HttpParams } from "@angular/common/http";
import { Component, ElementRef, Host, HostBinding, Input, OnChanges, OnDestroy, OnInit, Optional } from "@angular/core";
import { PageTitlePillComponent } from "@app/directives/_layout/_page-design/page-title-pill/page-title-pill.component";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IVehicle } from "@app/interfaces/IVehicle";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { HTMLService } from "@app/services/htmlService.service";
import { TranslationService } from "@app/services/translation.service";
import { lastValueFrom } from "rxjs";
import './camera-signal-strength.component.scss';

export interface ISignalStatus {
    SignalStrength3g: number;
    SignalStrength4g: number;

    Has3gCapabilities: boolean;
    Has4gCapabilities: boolean;

    WifiModuleStatus: boolean;
    SignalStrengthWifi: number;

    IgnitionStatus: boolean;
    RecordingStatus: number[]
}

@Component({
    selector: 'camera-signal-strength',
    template: require('./camera-signal-strength.component.html')
})
export class CameraSignalStrengthComponent implements OnInit, OnChanges, OnDestroy {
    @Input() vehicle: IVehicle;
    @HostBinding('class') signalStrengthClass: string;

    // ---

    camera: IPeripheral;

    signalStatus: ISignalStatus;
    signalType = this.translationService.t("Common.Loading");
    signalStrength = 3;

    intervalSubscription: ReturnType<typeof setInterval>;
    timeout: ReturnType<typeof setTimeout>;

    constructor(
        @Optional() @Host() private readonly pill: PageTitlePillComponent,
        private readonly el: ElementRef<HTMLElement>,
        private readonly httpClient: HttpClient,
        private readonly htmlService: HTMLService,
        private readonly translationService: TranslationService) {
    }

    ngOnInit() {
        this.updateDataAfterInterval();
    }

    ngOnChanges() {

        if (!this.vehicle) {
            return;
        }

        this.camera = this.vehicle.peripherals?.find(p => p.Product.IsCameraProduct);

        if (!this.cameraSupportsSignalStrength()) {
            this.hideComponent();
            return;
        }

        if (!this.signalStatus) {
            this.showLoadingIndicator();
        }

        this.updateData();
    }

    ngOnDestroy() {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;

        }

        if (this.intervalSubscription) {
            clearInterval(this.intervalSubscription);
            this.intervalSubscription = null;
        }
    }

    // ---

    private updateDataAfterInterval() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
            this.updateData();
        }, 10_000);
    }

    private updateData() {
        if (!this.cameraSupportsSignalStrength()) {
            return;
        }

        this.getSignalStrength(this.camera.Serial)
            .then(status => {
                this.signalStatus = status;

                this.parseSignalStrengthResponse();
            })
            .catch(() => {
                this.signalStatus = {} as any;
                this.signalStrength = 0;
                this.signalType = this.translationService.t("CameraSignalStrength.NoSignal");
            })
            .finally(() => {
                this.updateDataAfterInterval();
            });
    }

    private getSignalStrength(serial: string, invalidateCache = false): Promise<ISignalStatus> {
        const options = { params: {} as HttpParams }

        //options.params["minsToLive"] = 30;
        //options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<ISignalStatus>(`/api/ftcloudapi/signal/${serial}`, options));
    }

    private parseSignalStrengthResponse() {
        // This currently wont work due to broken API, leaving in for now
        if (this.signalStatus.SignalStrength4g > 0) {

            this.signalType = "4G";
            this.signalStrength = this.signalStatus.SignalStrength4g;
        }
        else if (this.signalStatus.SignalStrength3g > 0) {

            this.signalType = "3G";
            this.signalStrength = this.signalStatus.SignalStrength3g;
        }
        else {
            this.signalStrength = 0;
            this.signalType = this.translationService.t("CameraSignalStrength.NoSignal");
        }

        // This is temporary whilst API is broken
        if (this.signalStrength >= 4) {
            this.signalType = this.translationService.t("CameraSignalStrength.GoodSignal");
        }
        else if (this.signalStrength >= 2) {
            this.signalType = this.translationService.t("CameraSignalStrength.OKSignal");
        }
        else if (this.signalStrength > 0) {
            this.signalType = this.translationService.t("CameraSignalStrength.PoorSignal");
        }

        this.signalStrengthClass = `strength-${this.signalStrength}`;
    }

    private showLoadingIndicator() {
        if (this.intervalSubscription) {
            clearInterval(this.intervalSubscription);
        }

        this.intervalSubscription = setInterval(() => {
            if (this.signalStatus) {
                clearInterval(this.intervalSubscription);
                return;
            }

            if (++this.signalStrength > 5) {
                this.signalStrength = 1;
            }
        }, 500);
    }

    private hideComponent() {
        var elementToHide = this.el.nativeElement;

        if (this.pill) {
            elementToHide = this.htmlService.getParentByTag(this.el.nativeElement, "page-title-pill");
        }

        elementToHide.style.display = "none";
    }

    private cameraSupportsSignalStrength(): boolean {
        return this.camera?.Product?.GroupId === ProductVendor.Streamax;
    }
}
